import PropTypes from 'prop-types';
import { IconLatestResearch } from '@medifind/icons';
import { Skeleton } from '@medifind/shared-basic-components';
import { Card, CardActionButton } from './card-components';
import CardIcon from './card-components/CardIcon';
import styles from './PublicationCardV3.module.scss';

const PublicationCardV3 = ({
  publication,
  personSlug,
  orgId,
  personId,
  projectId,
  codingSlug,
  codingType,
  priority,
  hideIcon = false,
  hideSummary,
  conditionLabel = 'Condition',
  category,
  classes = {},
  smallAt,
}) => {
  const { publicationId, isPublication, journal, title, date, numPatients, treatment, codingName, summary, isGuideline } =
    publication || {};

  const createHref = () => {
    if (!publicationId) return null;
    if (process.env.NX_APP === 'MediFind') {
      const pubTypeUrl = isPublication ? 'publication' : 'treatment-advance';
      if (personId && projectId) {
        return `/doctors/${personSlug}/${personId}/${pubTypeUrl}/${publicationId}`;
      } else if (projectId) {
        const codingUrl = codingType === 'procedure' ? 'procedures' : 'conditions';
        return `/${codingUrl}/${codingSlug}/${projectId}/${pubTypeUrl}/${publicationId}`;
      } else {
        return `/articles/${pubTypeUrl}/${publicationId}`;
      }
    } else if (personId) {
      return `/people/${personId}/${isGuideline ? 'guideline' : 'publication'}/${publicationId}`;
    } else if (orgId) {
      return `/organizations/${orgId}/${category}/${isGuideline ? 'guideline' : 'publication'}/${publicationId}`;
    } else if (publicationId) {
      return `/articles/${isGuideline ? 'guideline' : 'publication'}/${publicationId}`;
    }
    return null;
  };

  const href = createHref();
  return (
    <Card
      id={`card_${isPublication ? 'publication' : 'treatment-advance'}_${publicationId}`} // Id is used by GA, don't change
      priority={priority}
      hideIcon={hideIcon}
      smallAt={smallAt}
      icon={
        <CardIcon
          className={classes.icon}
          color={'#645da2'}
          icon={<IconLatestResearch width={80} height={80} style={{ borderRadius: '50%' }} padding={false} showBoxShadow={false} />}
        />
      }
      title={title}
      link={{ to: href }}
      rel="nofollow"
      hasData={!!publication}
      metadata={{
        keyValue: [
          !isPublication && { label: `${conditionLabel}:`, value: codingName },
          { label: 'Journal:', value: journal },
          !isPublication && { label: `Treatment Used:`, value: treatment },
          !isPublication && { label: `Number of Patients:`, value: numPatients },
          { label: 'Published:', value: date },
        ],
      }}
      buttons={
        <CardActionButton
          noWrapping={false}
          disabled={!publicationId}
          label={isGuideline ? 'VIEW GUIDELINE' : 'VIEW PUBLICATION'}
          to={href}
          rel="nofollow"
        />
      }
    >
      {/* body/data */}
      {!hideSummary && (summary || !publication) ? (
        <div>
          {/* TODO not ellipses/toggle expand */}
          {publication ? (
            <div className={styles['summary']}>
              <b>Summary: </b>
              {summary.length > 400 ? summary.slice(0, 400) + '...' : summary}
            </div>
          ) : (
            <Skeleton count={4} />
          )}
        </div>
      ) : null}
    </Card>
  );
};

PublicationCardV3.propTypes = {
  projectId: PropTypes.number,
  codingSlug: PropTypes.string,
  codingType: PropTypes.string,
  personSlug: PropTypes.string,
  personId: PropTypes.number,
  orgId: PropTypes.number,
  publication: PropTypes.shape({
    date: PropTypes.string,
    dateModify: PropTypes.string,
    isPublication: PropTypes.bool,
    journal: PropTypes.string,
    journalTitle: PropTypes.string,
    meshList: PropTypes.array,
    pubType: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    publicationDate: PropTypes.number,
    publicationId: PropTypes.any,
    publicationPersonifyId: PropTypes.number,
    pubmedId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sourceType: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    url: PropTypes.string,
  }),
  id: PropTypes.any,
  style: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  smallAt: PropTypes.array,
  priority: PropTypes.number,
  absoluteUrl: PropTypes.string,
  hideIcon: PropTypes.bool,
  hideSummary: PropTypes.bool,
  conditionLabel: PropTypes.string,
  category: PropTypes.string,
  classes: PropTypes.object,
};

export { PublicationCardV3 as default, PublicationCardV3 };
